import {
  useAppSelector,
  useAppDispatch,
  setScheduleSlots,
  setOrderInfoLoading,
  setFulfillmentToTimeError,
} from '@/app/store';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getOrderItemsFromMenuItems } from '@/features';
import { useCallback, useEffect, useMemo } from 'react';
import { getLocalizedReduxErrorMessage } from '@/shared/lib';
import { ClientLocationDto, ServiceTypeEnum } from '@/app/store/api/restApi';
import { generateTimeIntervals, useGetLocationScheduleMutation } from '@/entities';

export const useActualiseTakeoutTimeSlots = (currentLocation: ClientLocationDto | undefined) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('errors');
  const { t: tBasket } = useTranslation('basket');
  const { t: tValidation } = useTranslation('validation');
  const { products, unavailableProductIds } = useAppSelector((state) => state.basket);
  const { fulfillmentToTime } = useAppSelector((state) => state.order);

  const orderedItems = useMemo(
    () => getOrderItemsFromMenuItems(products, unavailableProductIds),
    [products, unavailableProductIds],
  );

  const [getLocationSchedule] = useGetLocationScheduleMutation();

  const actualiseTakeoutSchedule = useCallback(async () => {
    if (currentLocation?.takeout?.orderOnTime && orderedItems.length > 0) {
      dispatch(setOrderInfoLoading(true));
      try {
        const locSchedule = await getLocationSchedule({
          id: currentLocation.id,
          getLocationScheduleDto: {
            serviceType: ServiceTypeEnum.Takeout,
            timeSlotsAdjustmentInfo: { orderedItems },
          },
        }).unwrap();

        if (!fulfillmentToTime && !locSchedule.data.asapAvailable) {
          if (locSchedule.data.dailyTimeslots.length > 0)
            dispatch(setFulfillmentToTimeError(tValidation('fulfillment.dateTimeRequired')));
          else dispatch(setFulfillmentToTimeError(tValidation('fulfillment.whenClosed')));
        } else dispatch(setFulfillmentToTimeError(undefined));

        if (locSchedule.data)
          dispatch(
            setScheduleSlots(
              generateTimeIntervals(locSchedule.data.dailyTimeslots, locSchedule.data.asapAvailable, tBasket),
            ),
          );
        else dispatch(setScheduleSlots([]));
      } catch (error) {
        dispatch(setScheduleSlots([]));
        toast.error(getLocalizedReduxErrorMessage(error ?? '', t));
      } finally {
        dispatch(setOrderInfoLoading(false));
      }
    } else {
      dispatch(setScheduleSlots([]));
      dispatch(setFulfillmentToTimeError(undefined));
    }
  }, [currentLocation, dispatch, getLocationSchedule, tValidation, tBasket, t, fulfillmentToTime, orderedItems]);

  useEffect(() => {
    actualiseTakeoutSchedule();
  }, [actualiseTakeoutSchedule]);
};
